import React, { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useAuth } from "./AuthContext";
import { useUserPreferences } from "./UserPreferencesContext";
import { supabase } from "../lib/supabase";
import { authFetch } from "../lib/auth";
import { getGradeTierProfile } from '@shared/gradeTier';
import { toast } from 'sonner';
import AchievementCelebration, {
  AchievementSummary,
  SUMMARY_THRESHOLD,
  type UnlockedAchievement,
} from "../components/achievements/AchievementCelebration";
import { acknowledgeInQueue, takeFresh, type QueueState } from "./achievementQueue";

/**
 * Celebrates badges the moment they're earned.
 *
 * The server grants badges from many places — some awarded in the background
 * (Ask Novari messages) where no response ever reaches the page — so the app
 * asks `/api/achievements/unseen` instead of relying on individual responses:
 *   - on sign-in, when the tab regains focus, and after `checkNow()`;
 *   - instantly when Supabase realtime reports a new user_achievements row.
 * Each badge shown is marked seen, which also cancels its push fallback.
 * More than SUMMARY_THRESHOLD waiting at once are shown together in one
 * summary instead of one dialog each.
 *
 * Celebrations wait while the student is mid-quiz or reading, and are off
 * when they turned "achievement alerts" off in Settings.
 */

interface AchievementContextType {
  /** Ask the server for new unlocks now (e.g. right after finishing a quiz). */
  checkNow: () => void;
  /**
   * A screen has just shown these badges itself (quiz results, the badge
   * page). Marks them seen on the server AND drops them from the pop-up queue,
   * so the celebration does not show them a second time.
   */
  acknowledge: (ids: string[]) => void;
}

const AchievementContext = createContext<AchievementContextType | undefined>(undefined);

const FOCUS_THROTTLE_MS = 60_000;

/** Pages where a pop-up would interrupt the work itself. */
function isBusyPath(pathname: string) {
  return (
    /^\/quiz\/session\//.test(pathname) ||
    pathname.startsWith("/papers/") ||
    /^\/quizzes?\/(?!new(?:\/|$))[^/]+/.test(pathname) ||
    pathname.startsWith("/lesson") ||
    pathname.startsWith("/chat") ||
    pathname.startsWith("/classroom") ||
    /^\/documents\/[^/]+\/reader/.test(pathname) ||
    pathname.startsWith("/onboarding") ||
    pathname.startsWith("/pricing") ||
    pathname.startsWith("/verify-payment")
  );
}

/**
 * Another dialog (page tour, appearance picker, help panel, a confirmation) is
 * already open. Badges wait for it rather than stacking on top. The celebration
 * dialogs mark themselves with data-achievement-dialog so they never block themselves.
 */
function foreignDialogOpen() {
  if (typeof document === "undefined") return false;
  return !!document.querySelector('[role="dialog"]:not([data-achievement-dialog]), [role="alertdialog"]');
}

export function AchievementProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { preferences } = useUserPreferences();
  const alertsOff = preferences?.notifications?.achievement_alerts === false;
  const isKids = getGradeTierProfile(preferences?.grade_level ?? preferences?.gradeLevel ?? '').isKids;
  const accountId = useRef(user?.id);
  accountId.current = user?.id;

  // One state object so acknowledging a badge cannot leave the queue and the
  // position pointing at different things.
  const [{ queue, index }, setQueueState] = useState<QueueState<UnlockedAchievement>>({ queue: [], index: 0 });
  const setQueue = useCallback(
    (next: UnlockedAchievement[] | ((q: UnlockedAchievement[]) => UnlockedAchievement[])) =>
      setQueueState((s) => ({ ...s, queue: typeof next === "function" ? next(s.queue) : next })),
    [],
  );
  const setIndex = useCallback((next: number) => setQueueState((s) => ({ ...s, index: next })), []);
  const [pathOk, setPathOk] = useState(() => (typeof window === "undefined" ? true : !isBusyPath(window.location.pathname)));
  const shownIds = useRef(new Set<string>());
  const lastCheck = useRef(0);
  const inFlight = useRef(false);

  const fetchUnseen = useCallback(async () => {
    if (!user || alertsOff || inFlight.current) return;
    inFlight.current = true;
    lastCheck.current = Date.now();
    try {
      const res = await authFetch("/api/achievements/unseen");
      if (!res.ok) return;
      const data = (await res.json()) as { unseen?: UnlockedAchievement[] };
      if (accountId.current !== user.id) return;
      const fresh = takeFresh(data.unseen ?? [], shownIds.current);
      if (fresh.length === 0) return;
      fresh.forEach((a) => shownIds.current.add(a.id));
      setQueue((q) => [...q, ...fresh]);
    } catch {
      /* offline: try again on the next focus */
    } finally {
      inFlight.current = false;
    }
  }, [user, alertsOff, setQueue]);

  // On sign-in.
  useEffect(() => {
    setQueue([]);
    setIndex(0);
    shownIds.current.clear();
  }, [user?.id, setQueue, setIndex]);

  useEffect(() => {
    if (!user) {
      setQueue([]);
      setIndex(0);
      shownIds.current.clear();
      return;
    }
    const t = window.setTimeout(fetchUnseen, 2500);
    return () => window.clearTimeout(t);
  }, [user, fetchUnseen, setQueue, setIndex]);

  // When the student comes back to the tab.
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === "visible" && Date.now() - lastCheck.current > FOCUS_THROTTLE_MS) void fetchUnseen();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [fetchUnseen]);

  // Instantly, when a badge row is inserted for this student.
  useEffect(() => {
    if (!user || alertsOff) return;
    let timer: number | undefined;
    const channel = supabase
      .channel(`achievements:${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "user_achievements", filter: `user_id=eq.${user.id}` },
        () => {
          // Several badges can land together; fetch once they've settled.
          window.clearTimeout(timer);
          timer = window.setTimeout(() => void fetchUnseen(), 1200);
        },
      )
      .subscribe();
    return () => {
      window.clearTimeout(timer);
      void supabase.removeChannel(channel);
    };
  }, [user, alertsOff, fetchUnseen]);

  // The provider sits outside the router, so watch the URL for busy pages.
  useEffect(() => {
    const update = () => setPathOk(!isBusyPath(window.location.pathname) && !foreignDialogOpen());
    const id = window.setInterval(update, 1500);
    window.addEventListener("popstate", update);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("popstate", update);
    };
  }, []);

  const visible = pathOk && !alertsOff;
  // Decided when the queue starts; badges arriving mid-step-through join it.
  const summarise = index === 0 && queue.length > SUMMARY_THRESHOLD;
  const current = visible && !isKids && !summarise ? queue[index] ?? null : null;

  const markSeen = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    void authFetch("/api/achievements/mark-seen", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ achievementIds: ids }),
    }).catch(() => undefined);
  }, []);

  const advance = useCallback(() => {
    const shown = queue[index];
    if (shown) markSeen([shown.id]);
    if (index + 1 >= queue.length) {
      setQueue([]);
      setIndex(0);
    } else {
      setIndex(index + 1);
    }
  }, [queue, index, markSeen, setQueue, setIndex]);

  const acknowledge = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    // Remember them even if the badge has not reached the queue yet: a
    // realtime fetch that is already in flight must not add it afterwards.
    ids.forEach((id) => shownIds.current.add(id));
    setQueueState((s) => acknowledgeInQueue(s, ids));
    markSeen(ids);
  }, [markSeen]);

  const closeSummary = useCallback(() => {
    markSeen(queue.map((a) => a.id));
    setQueue([]);
    setIndex(0);
  }, [queue, markSeen, setQueue, setIndex]);

  const viewAll = useCallback(() => {
    markSeen(queue.slice(index).map((a) => a.id));
    setQueue([]);
    setIndex(0);
    if (window.location.pathname !== "/achievements") {
      window.history.pushState({}, "", "/achievements");
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
  }, [queue, index, markSeen, setQueue, setIndex]);

  // A child's next learning action stays available when rewards arrive.
  useEffect(() => {
    if (!isKids || !visible || queue.length === 0) return;
    toast(queue.length === 1 ? `You earned ${queue[0].name}` : `You earned ${queue.length} new badges`, {
      description: 'Find your badges in your profile.',
      action: { label: 'See badges', onClick: viewAll },
    });
    markSeen(queue.map(a => a.id));
    setQueue([]);
    setIndex(0);
  }, [isKids, visible, queue, markSeen, viewAll, setQueue, setIndex]);

  return (
    <AchievementContext.Provider value={{ checkNow: () => void fetchUnseen(), acknowledge }}>
      {children}
      <AchievementCelebration
        achievement={current}
        index={index}
        total={queue.length}
        onNext={advance}
        onViewAll={viewAll}
      />
      <AchievementSummary achievements={visible && !isKids && summarise ? queue : null} onClose={closeSummary} onViewAll={viewAll} />
    </AchievementContext.Provider>
  );
}

export function useAchievements() {
  const context = useContext(AchievementContext);
  if (context === undefined) {
    throw new Error("useAchievements must be used within an AchievementProvider");
  }
  return context;
}

/** For screens that may render outside the provider (tests, fixtures): acknowledging is then a no-op. */
export function useOptionalAchievements(): AchievementContextType | undefined {
  return useContext(AchievementContext);
}
